import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState({ loading: false, sent: false, error: '' });

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setError('Invalid verification link.');
        return;
      }

      try {
        await axios.post(`${API}/auth/verify-email`, { token });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(getErrorMessage(err, 'Verification failed. The link may have expired.'));
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    
    setResendStatus({ loading: true, sent: false, error: '' });
    
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      setResendStatus({ loading: false, sent: true, error: '' });
    } catch (err) {
      setResendStatus({ 
        loading: false, 
        sent: false, 
        error: getErrorMessage(err, 'Failed to resend verification email.')
      });
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <Card className="w-full max-w-md shadow-2xl border-gray-700 bg-gray-800">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img 
                src="/logo.png"
                alt="Gumba Logo"
                className="h-20 object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Verifying Email...</CardTitle>
            <CardDescription className="text-base text-gray-400">
              Please wait while we verify your email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <Card className="w-full max-w-md shadow-2xl border-gray-700 bg-gray-800">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-4 bg-green-500 rounded-full shadow-lg">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-green-400">Email Verified!</CardTitle>
            <CardDescription className="text-base text-gray-400">
              Your email has been successfully verified. You can now log in to your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
      <Card className="w-full max-w-md shadow-2xl border-gray-700 bg-gray-800">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 bg-red-500 rounded-full shadow-lg">
              <XCircle className="h-12 w-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-red-400">Verification Failed</CardTitle>
          <CardDescription className="text-base text-gray-400">
            {error}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Resend Verification Section */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4 text-yellow-500" />
              Resend Verification Email
            </h3>
            {resendStatus.sent ? (
              <p className="text-green-400 text-sm">
                ✓ If an account exists with that email, a new verification link has been sent.
              </p>
            ) : (
              <form onSubmit={handleResendVerification} className="space-y-3">
                <div>
                  <Label htmlFor="resend-email" className="text-gray-300 text-xs">Enter your email address</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                    required
                  />
                </div>
                {resendStatus.error && (
                  <p className="text-red-400 text-xs">{resendStatus.error}</p>
                )}
                <Button
                  type="submit"
                  disabled={resendStatus.loading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-sm"
                >
                  {resendStatus.loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    'Resend Verification Link'
                  )}
                </Button>
              </form>
            )}
          </div>
          
          <div className="flex gap-4">
            <Link to="/register" className="flex-1">
              <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-700">Register Again</Button>
            </Link>
            <Link to="/login" className="flex-1">
              <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">Go to Login</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
